import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { CreditCard, Search, Image as ImageIcon, Check, X, Download } from 'lucide-react';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import AdminPagination from '../../components/admin/AdminPagination';
import { fetchAllAdminPages } from '../../utils/fetchAllAdminPages';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPayments = () => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(0);
  const requestId = useRef(0);
  const [selectedImage, setSelectedImage] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); setDebouncedSearch(searchQuery.trim()); }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const fetchPayments = useCallback(async () => {
    const currentRequest = ++requestId.current;
    try {
      const response = await axios.get(`${API}/payments/admin/all`, {
        params: { page, search: debouncedSearch }, withCredentials: true
      });
      if (currentRequest !== requestId.current) return;
      setPayments(response.data.items);
      setTotal(response.data.total);
      setPages(response.data.pages);
      if (page > Math.max(1, response.data.pages)) setPage(Math.max(1, response.data.pages));
    } catch (error) {
      if (currentRequest !== requestId.current) return;
      console.error('Error fetching payments:', error);
      toast.error('Failed to load payments');
    } finally {
      if (currentRequest === requestId.current) setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => { fetchPayments(); }, [fetchPayments]);

  const handleUpdateStatus = async (paymentId, status) => {
    try {
      await axios.put(`${API}/payments/admin/${paymentId}/status`, { status }, { withCredentials: true });
      toast.success(`Payment marked as ${status}`);
      fetchPayments();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Failed to update status');
    }
  };

  const statusColors = {
    pending: 'bg-yellow-100 text-yellow-700 border border-yellow-200',
    completed: 'bg-green-100 text-green-700 border border-green-200',
    failed: 'bg-red-100 text-red-700 border border-red-200'
  };

  const filteredPayments = payments;

  const exportAllPayments = async () => {
    try {
      const allPayments = await fetchAllAdminPages(`${API}/payments/admin/all`, { search: debouncedSearch });
      downloadCSV(allPayments);
    } catch (error) {
      toast.error('Failed to export payments');
    }
  };

  // ── CSV Export ──
  const downloadCSV = (dataToExport) => {
    if (!dataToExport.length) {
      toast.error('No payments to export');
      return;
    }

    const escapeCSV = (val) => {
      if (val === null || val === undefined) return '';
      const str = String(val);
      if (str.includes(',') || str.includes('"') || str.includes('\n')) {
        return '"' + str.replace(/"/g, '""') + '"';
      }
      return str;
    };

    const headers = [
      'Payment ID',
      'Date & Time',
      'Customer Name',
      'Customer Email',
      'Order ID',
      'Order Type',
      'Amount (Rs)',
      'Payment Status',
      'UTR / TXN ID',
      'Screenshot URL',
    ];

    const rows = dataToExport.map(payment => [
      payment.payment_id,
      payment.created_at ? new Date(payment.created_at).toLocaleString('en-IN') : '',
      payment.user?.name || '',
      payment.user?.email || '',
      payment.order_id || '',
      payment.order_type || '',
      payment.amount || '',
      payment.status || 'pending',
      payment.txn_id || '',
      payment.screenshot_url || '',
    ].map(escapeCSV).join(','));

    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `payments_report_${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${dataToExport.length} payments as CSV`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      
      {/* Search */}
      <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
          <Input
            placeholder="Search by email, payment ID, or UTR..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="payment-search"
          />
        </div>
        {/* Download buttons */}
        <div className="flex gap-2">
          <button
            onClick={() => downloadCSV(filteredPayments)}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
            title="Download current search results as CSV"
            data-testid="download-filtered-payments-csv"
          >
            <Download className="w-4 h-4" />
            Export Filtered ({filteredPayments.length})
          </button>
          <button
            onClick={exportAllPayments}
            className="flex items-center gap-2 px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-semibold transition-colors shadow-sm"
            title="Download all matching payments as CSV"
            data-testid="download-all-payments-csv"
          >
            <Download className="w-4 h-4" />
            Export All ({total})
          </button>
        </div>
      </div>

      {/* Payments Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Payment ID</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">User</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Order Detail</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Verification (UTR)</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Amount</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Status</th>
                <th className="px-6 py-4 text-xs font-bold text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredPayments.map((payment) => (
                <motion.tr
                  key={payment.payment_id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="hover:bg-gray-50/50 transition-colors"
                  data-testid={`payment-row-${payment.payment_id}`}
                >
                  <td className="px-6 py-4">
                    <span className="font-mono text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">{payment.payment_id.slice(-8).toUpperCase()}</span>
                    <div className="text-xs text-gray-400 mt-1">{new Date(payment.created_at).toLocaleDateString()}</div>
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-semibold text-gray-900">{payment.user?.name}</p>
                      <p className="text-xs text-gray-500">{payment.user?.email}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex flex-col gap-1.5">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-[11px] font-black uppercase tracking-wider shadow-sm ${
                        payment.order_type === 'booking' 
                          ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                          : 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                      }`}>
                        {payment.order_type === 'booking' ? (
                          <><span className="text-orange-500">🕉️</span> PUJA BOOKING</>
                        ) : (
                          <><span className="text-indigo-500">🛒</span> STORE ORDER</>
                        )}
                      </span>
                      <span className="font-mono text-[10px] text-gray-400 font-bold ml-1">
                        #{payment.order_id?.slice(-8).toUpperCase()}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-2">
                      {payment.txn_id ? (
                        <span className="font-mono text-xs font-bold text-indigo-600 bg-indigo-50 px-2 py-1 rounded">{payment.txn_id}</span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">No UTR</span>
                      )}
                      
                      {payment.screenshot_url && (
                        <button 
                          onClick={() => setSelectedImage(payment.screenshot_url)}
                          className="p-1.5 text-gray-500 hover:text-blue-500 hover:bg-blue-50 rounded-md transition-colors"
                          title="View Screenshot"
                        >
                          <ImageIcon className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span className="font-black text-gray-900">₹{payment.amount?.toLocaleString()}</span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-bold ${statusColors[payment.status || 'pending']}`}>
                      {(payment.status || 'pending').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    {(payment.status === 'pending' || !payment.status) && (
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleUpdateStatus(payment.payment_id, 'completed')}
                          className="p-2 bg-green-50 text-green-600 hover:bg-green-500 hover:text-white rounded-lg transition-colors"
                          title="Approve"
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleUpdateStatus(payment.payment_id, 'failed')}
                          className="p-2 bg-red-50 text-red-600 hover:bg-red-500 hover:text-white rounded-lg transition-colors"
                          title="Reject"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
        
        {filteredPayments.length === 0 && (
          <div className="text-center py-16">
            <div className="w-16 h-16 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-4 border border-gray-100">
              <CreditCard className="w-8 h-8 text-gray-400" />
            </div>
            <h3 className="text-gray-900 font-bold mb-1">No payments found</h3>
            <p className="text-gray-500 text-sm">Waiting for incoming manual payments</p>
          </div>
        )}
      </div>

      <AdminPagination page={page} pages={pages} total={total} onPageChange={setPage} />

      {/* Summary Stats */}
      <div className="grid md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <p className="text-sm font-medium text-gray-500 mb-1">Total Matching Payments</p>
          <p className="text-3xl font-black text-gray-900">{total}</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <p className="text-sm font-medium text-gray-500 mb-1">Pending on This Page</p>
          <p className="text-3xl font-black text-amber-500">{payments.filter(p => p.status === 'pending' || !p.status).length}</p>
        </div>
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
          <p className="text-sm font-medium text-gray-500 mb-1">Verified on This Page</p>
          <p className="text-3xl font-black text-emerald-500">{payments.filter(p => p.status === 'completed').length}</p>
        </div>
        <div className="bg-gradient-to-br from-[#FF7B00] to-[#D4AF37] rounded-xl p-5 shadow-sm text-white">
          <p className="text-sm font-medium text-white/80 mb-1">Revenue on This Page</p>
          <p className="text-3xl font-black">
            ₹{payments.filter(p => p.status === 'completed').reduce((sum, p) => sum + (p.amount || 0), 0).toLocaleString()}
          </p>
        </div>
      </div>

      {/* Image Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4 backdrop-blur-sm"
            onClick={() => setSelectedImage(null)}
          >
            <motion.div
              initial={{ scale: 0.95 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.95 }}
              className="relative max-w-4xl max-h-[90vh] bg-white rounded-xl overflow-hidden p-2"
              onClick={e => e.stopPropagation()}
            >
              <button 
                onClick={() => setSelectedImage(null)}
                className="absolute top-4 right-4 bg-black/50 hover:bg-black text-white p-2 rounded-full transition-colors z-10"
              >
                <X className="w-5 h-5" />
              </button>
              <img src={selectedImage} alt="Payment Screenshot" className="max-w-full max-h-[85vh] object-contain rounded-lg" />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

    </div>
  );
};

export default AdminPayments;
